/**
 * @module engine/routing
 * @summary Connector path routing + drawing (manhattan / curved).
 * @description
 *   `routePts` (pure) computes the elbow points for a connector, honouring any
 *   user-dragged waypoint (midX/midY). `drawArrow` (pure) paints an arrowhead.
 *   `drawConn` paints a full connector (line, dashes, arrowheads, label,
 *   selection handles); it reads `S` for the shape lookup, zoom and routeMode.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 3150-3227.
 */
import type { Connection, Point, Shape } from '../types';
import { S } from './state';
import { getPortAnchors } from './geometry';
import { hasPortAnchors, type PortAnchor } from './shapes/ports';

/**
 * How far pipework runs straight out of a directional port before it may turn.
 * Long enough to read as a pipe stub at 100% zoom, short enough not to collide
 * with neighbouring equipment on a dense sheet.
 */
const STUB = 14;

/** Collapse duplicate and collinear vertices so no zero-length segments survive. */
function simplify(pts: Point[]): Point[] {
  const out: Point[] = [];
  for (const p of pts) {
    const last = out[out.length - 1];
    if (last && Math.abs(last.x - p.x) < 0.01 && Math.abs(last.y - p.y) < 0.01) continue;
    out.push(p);
  }
  for (let i = 1; i < out.length - 1; ) {
    const a = out[i - 1];
    const b = out[i];
    const c = out[i + 1];
    const cross = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
    if (Math.abs(cross) < 0.01) out.splice(i, 1);
    else i++;
  }
  return out.length >= 2 ? out : pts;
}

/** True when the anchor's normal is (near) horizontal. */
function isHoriz(n: Point): boolean {
  return Math.abs(n.x) > Math.abs(n.y);
}

/**
 * Route p1→p2 so the run leaves each port ALONG its outward normal.
 *
 * The plain mid-X router turns the instant it leaves a port, which on an
 * up-facing elbow mouth produces a sideways stub right at the joint. Here each
 * end gets a short collinear stub first, then the two stub ends are joined with
 * the Manhattan leg their orientations imply. The last segment therefore always
 * runs squarely into the port, which is also what makes the arrowhead point
 * down the pipe axis instead of across it.
 */
function routeDirectional(p1: Point, p2: Point, a1: PortAnchor, a2: PortAnchor): Point[] {
  // Never let the stubs overshoot each other on a short run.
  const span = Math.max(Math.abs(p2.x - p1.x), Math.abs(p2.y - p1.y));
  const L = Math.max(4, Math.min(STUB, span * 0.4));
  const q1 = { x: p1.x + a1.n.x * L, y: p1.y + a1.n.y * L };
  const q2 = { x: p2.x + a2.n.x * L, y: p2.y + a2.n.y * L };
  const h1 = isHoriz(a1.n);
  const h2 = isHoriz(a2.n);
  // Faces that look at each other can be joined head-on; faces pointing the same
  // way cannot, or the run doubles back through its own stub.
  const opposed = a1.n.x * a2.n.x + a1.n.y * a2.n.y < 0;
  let mid: Point[];
  if (h1 && h2) {
    if (Math.abs(q1.y - q2.y) < 1) {
      // Aligned: one clean straight pipe, no intermediate vertices at all.
      if (opposed) return [p1, p2];
      // Same-facing and collinear — step the crossing leg off-axis to get round.
      const oy = q1.y - STUB;
      mid = [
        { x: q1.x, y: oy },
        { x: q2.x, y: oy },
      ];
    } else {
      const mx = (q1.x + q2.x) / 2;
      mid = [
        { x: mx, y: q1.y },
        { x: mx, y: q2.y },
      ];
    }
  } else if (!h1 && !h2) {
    // Both faces vertical: mirror of the above about the diagonal.
    if (Math.abs(q1.x - q2.x) < 1) {
      if (opposed) return [p1, p2];
      const ox = q1.x - STUB;
      mid = [
        { x: ox, y: q1.y },
        { x: ox, y: q2.y },
      ];
    } else {
      const my = (q1.y + q2.y) / 2;
      mid = [
        { x: q1.x, y: my },
        { x: q2.x, y: my },
      ];
    }
  } else if (h1) {
    // Horizontal out, vertical in: single corner.
    mid = [{ x: q2.x, y: q1.y }];
  } else {
    mid = [{ x: q1.x, y: q2.y }];
  }
  return simplify([p1, q1, ...mid, q2, p2]);
}

/**
 * Compute the poly-line points connecting p1→p2 for connector `cn`.
 * Pass `a1`/`a2` (the endpoint port anchors) to get direction-aware routing;
 * without them the original mid-X behaviour is used unchanged.
 */
export function routePts(
  p1: Point,
  p2: Point,
  cn?: Connection,
  a1?: PortAnchor,
  a2?: PortAnchor,
): Point[] {
  // Custom horizontal-only waypoint (midX set, midY unset).
  if (cn && cn.midX !== undefined && cn.midY === undefined) {
    return [p1, { x: cn.midX, y: p1.y }, { x: cn.midX, y: p2.y }, p2];
  }
  // Full custom waypoint.
  if (cn && cn.midX !== undefined && cn.midY !== undefined) {
    return [
      p1,
      { x: cn.midX, y: p1.y },
      { x: cn.midX, y: cn.midY },
      { x: cn.midX, y: p2.y },
      p2,
    ];
  }
  // Reached only when there is no hand-dragged waypoint (handled above): a
  // user-placed midpoint always wins over automatic routing.
  if (a1 && a2) return routeDirectional(p1, p2, a1, a2);
  if (Math.abs(p1.y - p2.y) < 4) return [p1, p2];
  const midX = (p1.x + p2.x) / 2;
  return [p1, { x: midX, y: p1.y }, { x: midX, y: p2.y }, p2];
}

/**
 * Resolve a connector to its endpoint anchors + poly-line, or null if either
 * shape is gone. The single source of truth for connector geometry — drawing,
 * hit-testing and the segment handles all read it, so they can never disagree.
 *
 * Direction-aware routing is enabled only when an endpoint sits on a shape that
 * declares its own faces (`PORT_ANCHORS`, i.e. elbows). Everything else keeps
 * the historical mid-X path, so existing drawings are untouched.
 */
export function connGeom(
  cn: Connection,
): { f: Shape; t: Shape; fp: Point; tp: Point; pts: Point[] } | null {
  const f = S.shapes.find((s) => s.id === cn.fromId);
  const t = S.shapes.find((s) => s.id === cn.toId);
  if (!f || !t) return null;
  const fa = getPortAnchors(f)[cn.fromPort || 'E'];
  const ta = getPortAnchors(t)[cn.toPort || 'W'];
  const directional = hasPortAnchors(f.type) || hasPortAnchors(t.type);
  const pts = directional
    ? routePts(fa.p, ta.p, cn, fa, ta)
    : routePts(fa.p, ta.p, cn);
  return { f, t, fp: fa.p, tp: ta.p, pts };
}

/**
 * Where a connector's label sits: the midpoint of its LONGEST straight segment.
 *
 * Not a path vertex — vertices are corners and, on a two-point run, the target
 * port itself, which is exactly where connectors converge and labels pile up on
 * each other. The longest leg is also the one with the most clear space either
 * side, so the knocked-out background has room. On a straight run this is simply
 * the middle of the line; for a curve, `pts` is the chord and its midpoint
 * coincides with the bezier's t=0.5 point.
 */
export function labelAnchor(pts: Point[]): Point {
  let bi = 0;
  let bd = -1;
  for (let i = 0; i < pts.length - 1; i++) {
    const d = Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
    if (d > bd) {
      bd = d;
      bi = i;
    }
  }
  return {
    x: (pts[bi].x + pts[bi + 1].x) / 2,
    y: (pts[bi].y + pts[bi + 1].y) / 2,
  };
}

/** Draw a filled arrowhead at `to`, pointing away from `from`. */
export function drawArrow(
  c: CanvasRenderingContext2D,
  from: Point,
  to: Point,
  color: string,
): void {
  const a = Math.atan2(to.y - from.y, to.x - from.x);
  const sz = 9;
  c.fillStyle = color;
  c.setLineDash([]);
  c.beginPath();
  c.moveTo(to.x, to.y);
  c.lineTo(to.x - sz * Math.cos(a - 0.42), to.y - sz * Math.sin(a - 0.42));
  c.lineTo(to.x - sz * Math.cos(a + 0.42), to.y - sz * Math.sin(a + 0.42));
  c.closePath();
  c.fill();
}

/** Draw a full connector. `sel` highlights it and shows segment handles. */
/**
 * @param rate  Throughput on this stream, already formatted, or '' for a run
 *   the balance has nothing to say about. Passed in rather than looked up here
 *   so the whole drawing is balanced once per frame instead of once per line.
 * @param material  What the stream carries — a raw material off a feeder, or
 *   the finished good past the pelletizer. Its own row rather than run together
 *   with the rate: a caption sits ON the line, so width is what runs out first,
 *   and "PVC-70-INS-90C · 1485 kg/h" on one row collides with its neighbours
 *   where two short rows do not.
 */
export function drawConn(
  c: CanvasRenderingContext2D,
  cn: Connection,
  sel: boolean,
  rate = '',
  material = '',
): void {
  const g = connGeom(cn);
  if (!g) return;
  const { fp, tp } = g;
  const useCurve = cn.routeMode === 'curved' || S.routeMode === 'curved';
  const pts = useCurve ? [fp, tp] : g.pts;
  c.save();
  // Colour: explicit cn.color, else grey for dashed / black for solid.
  const isDashed =
    cn.lineStyle === 'dashed' || cn.lineStyle === 'dashed-short' || cn.lineStyle === 'dash-dot';
  const color = cn.color ? cn.color : isDashed ? '#aaaaaa' : '#1a1a1a';
  c.strokeStyle = color;
  c.fillStyle = color;
  // Round caps/joins on solid runs so a pipe meets an elbow face (and its own
  // corners) without a notch — the connector is thinner than the pipe symbol it
  // docks against. Dashed/dotted styles keep butt caps or the dashes fatten.
  c.lineJoin = 'round';
  if (!isDashed && cn.lineStyle !== 'dotted') c.lineCap = 'round';
  c.lineWidth = cn.lineWidth || (isDashed ? 1.0 : 1.5);
  // Zoom-aware dash patterns so dashes look consistent at any zoom.
  const dz = 1 / S.zoom;
  if (cn.lineStyle === 'dashed') c.setLineDash([12 * dz, 6 * dz]);
  else if (cn.lineStyle === 'dashed-short') c.setLineDash([6 * dz, 4 * dz]);
  else if (cn.lineStyle === 'dotted') c.setLineDash([2 * dz, 4 * dz]);
  else if (cn.lineStyle === 'dash-dot') c.setLineDash([12 * dz, 4 * dz, 2 * dz, 4 * dz]);
  else c.setLineDash([]);
  c.beginPath();
  if (useCurve) {
    const cp1x = fp.x + (tp.x - fp.x) * 0.5;
    const cp2x = fp.x + (tp.x - fp.x) * 0.5;
    c.moveTo(fp.x, fp.y);
    c.bezierCurveTo(cp1x, fp.y, cp2x, tp.y, tp.x, tp.y);
  } else {
    pts.forEach((p, i) => (i === 0 ? c.moveTo(p.x, p.y) : c.lineTo(p.x, p.y)));
  }
  c.stroke();
  c.setLineDash([]);
  if (cn.arrowHead !== 'none') drawArrow(c, pts[pts.length - 2], pts[pts.length - 1], color);
  if (cn.arrowHead === 'both') drawArrow(c, pts[1], pts[0], color);
  if (cn.label || rate || material) {
    const m = labelAnchor(pts);
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    // The caption is the engineer's own words; what the stream carries and how
    // much of it are computed figures. Stacking all three rather than running
    // them together keeps that distinction, and keeps a long name from pushing
    // the number off the line.
    const rows: { text: string; font: string; ink: string }[] = [];
    if (cn.label) rows.push({ text: cn.label, font: 'bold 8.5px Segoe UI', ink: color });
    if (material) rows.push({ text: material, font: '600 8px Segoe UI', ink: '#1e4b85' });
    if (rate) rows.push({ text: rate, font: '600 8px ui-monospace, Consolas, monospace', ink: '#1f6f43' });
    let wide = 0;
    for (const r of rows) {
      c.font = r.font;
      wide = Math.max(wide, c.measureText(r.text).width);
    }
    const h = rows.length * 11 + 3;
    // Knock the pipe out behind the text (measured, not guessed from the
    // character count) so the label stays readable on any run.
    c.fillStyle = '#fff';
    c.fillRect(m.x - wide / 2 - 3, m.y - h / 2, wide + 6, h);
    rows.forEach((r, i) => {
      c.font = r.font;
      c.fillStyle = r.ink;
      c.fillText(r.text, m.x, m.y - h / 2 + 6 + i * 11);
    });
  }
  if (sel) {
    c.strokeStyle = 'rgba(0,120,212,0.5)';
    c.lineWidth = 4 / S.zoom;
    c.setLineDash([]);
    c.beginPath();
    pts.forEach((p, i) => (i === 0 ? c.moveTo(p.x, p.y) : c.lineTo(p.x, p.y)));
    c.stroke();
    // Draggable segment-midpoint handles.
    for (let si = 0; si < pts.length - 1; si++) {
      const hx = (pts[si].x + pts[si + 1].x) / 2;
      const hy = (pts[si].y + pts[si + 1].y) / 2;
      const isMiddle = si === Math.floor((pts.length - 1) / 2);
      c.fillStyle = isMiddle ? '#0078d4' : 'rgba(0,120,212,0.6)';
      c.strokeStyle = '#fff';
      c.lineWidth = 1.5;
      const hs = isMiddle ? 6 : 4;
      c.fillRect(hx - hs, hy - hs, hs * 2, hs * 2);
      c.strokeRect(hx - hs, hy - hs, hs * 2, hs * 2);
    }
  }
  c.restore();
}
